import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Card } from "~components/Card"
import PageContent from "~components/PageContent"
import MessageModal from "~components/messages/MessageModal"
import MessagesList from "~components/messages/MessagesList"
import TablePaginator from "~components/TablePaginator"
import MessageRequestModal from "~components/messages/MessageRequestModal"
import { ButtonPrimarySoftLarge } from "~styled/button"
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import IsAuthorized from "~rbac/isAuthorized"
import MessageService from "~services/MessageService"
import { MessagePinLevel, MessageShape } from "~shapes/message"
import { OnRequestCloseProps } from "~shapes/modal"
import { paginatorInitState, TablePaginatorShape } from "~shapes/api-table"
import { ContainerRight, ContainerSpaceBetween } from "~styled/containers"
import TableSearchField from "~components/TableSearchField"
import PageHeader from "~components/PageHeader"
import { useMutation } from "react-query"
import useMessages from "~hooks/useMessages"
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import useCompanyMessages from "~hooks/useCompanyMessages"
import BackLink from "~components/BackLink"
import PATHS, { getFullPath } from "~constants/paths"

const Messages = () => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const state = location.state as { company?: any } | null
  const company = state?.company
  const navigateFromCompanies = !!id && !!company
  const navigate = useNavigate()
  const [message, setMessage] = useState<MessageShape | undefined>(undefined)
  const [messages, setMessages] = useState<MessageShape[]>([])
  const [paginator, setPaginator] =
    useState<TablePaginatorShape>(paginatorInitState)
  const [isModalOpened, setIsModalOpened] = useState<boolean>(false)
  const [options, setOptions] = useState({ sort: "publishedAt,desc", size: 15 })
  const {
    data: messagesData,
    refetch: fetchMessages,
    isFetching: isLoadingMessages,
    isError: isErrorFetchingMessages,
  } = useMessages(options)
  const {
    data: companyMessages,
    refetch: fetchCompanyMessages,
    isFetching: isLoadingCompanyMessages,
    isError: isErrorFetchingCompanyMessages,
  } = useCompanyMessages(id || "", options)

  const { mutateAsync: mutateMessage, isError: errorUpdatingMessage } =
    useMutation("message", (payload: Partial<MessageShape>) =>
      MessageService.patchMessage(payload),
    )

  useEffect(() => {
    if (isErrorFetchingMessages || isErrorFetchingCompanyMessages) {
      toast.error(`Failed to load the messages`)
      setPaginator(paginatorInitState)
      setMessages([])
    }
    if (errorUpdatingMessage) toast.error(`Failed to update the message`)
  }, [
    isErrorFetchingMessages,
    errorUpdatingMessage,
    isErrorFetchingCompanyMessages,
  ])

  useEffect(() => {
    if (navigateFromCompanies) {
      ;(async () => await fetchCompanyMessages())()
    } else {
      ;(async () => await fetchMessages())()
    }
  }, [options])

  useDeepCompareEffectNoCheck(() => {
    if (messagesData && !navigateFromCompanies) {
      setPaginator(messagesData.page)
      setMessages(messagesData._embedded.posts)
    }
  }, [messagesData])

  useDeepCompareEffectNoCheck(() => {
    if (companyMessages && navigateFromCompanies) {
      const paginatorAux: TablePaginatorShape = {
        size: companyMessages.size,
        number: companyMessages.currentPage,
        totalPages: companyMessages.totalPages,
        totalElements: companyMessages.totalItems,
      }
      setPaginator(paginatorAux)
      setMessages(companyMessages.posts)
    }
  }, [companyMessages])

  const handlePageChange = (page: number) => {
    setOptions({ ...options, ...{ page: page } })
  }

  const handleEditMessage = (message: MessageShape) => {
    setMessage(message)
    setIsModalOpened(true)
  }

  const handleOpenMessageModal = () => {
    setMessage(undefined)
    setIsModalOpened(true)
  }

  const handleOnRequestClose = ({ shouldRefreshList }: OnRequestCloseProps) => {
    if (shouldRefreshList)
      setOptions({ ...options, ...{ page: paginator.number } })
    setIsModalOpened(false)
  }

  const handleMessageView = (message: MessageShape) => {
    const navOptions = navigateFromCompanies
      ? { state: { navigateFrom: "companies" } }
      : {}
    navigate(`${getFullPath(PATHS.messages)}/${message.id}`, navOptions)
  }

  const handleUpdateMessage = async (data: Partial<MessageShape>) => {
    const response = await mutateMessage(data)
    const updatedMessages = messages.map(message => {
      if (message.id !== response.id) return message
      let object = {
        ...message,
        status: response.status,
      }
      if (navigateFromCompanies)
        object = { ...object, companyLevelPinned: response.companyLevelPinned }
      else
        object = {
          ...object,
          communicationLevelPinned: response.communicationLevelPinned,
        }
      return object
    })
    setMessages(updatedMessages)
  }

  return (
    <PageContent>
      {navigateFromCompanies && (
        <BackLink
          text="Back to Companies"
          path={`${getFullPath(PATHS.companies)}`}
        />
      )}
      <PageHeader
        title={navigateFromCompanies ? `${company.name} Messages` : `Messages`}
      />

      <Card>
        {!!company ? (
          <ContainerRight>
            <ButtonPrimarySoftLarge onClick={handleOpenMessageModal}>
              Create a new message
            </ButtonPrimarySoftLarge>
          </ContainerRight>
        ) : (
          <ContainerSpaceBetween style={{ marginBottom: 30 }}>
            <TableSearchField
              searchBy={["company.name"]}
              placeholder="SEARCH BY COMPANY NAME"
              callback={setOptions}
            />
            <ButtonPrimarySoftLarge onClick={handleOpenMessageModal}>
              Create a new message
            </ButtonPrimarySoftLarge>
          </ContainerSpaceBetween>
        )}
        <MessagesList
          company={company}
          messages={messages}
          isLoading={isLoadingMessages || isLoadingCompanyMessages}
          pinLevel={
            navigateFromCompanies
              ? MessagePinLevel.COMPANY_LEVEL
              : MessagePinLevel.COMMUNICATION_LEVEL
          }
          onClickMessageEdit={handleEditMessage}
          onChange={handleUpdateMessage}
          onClickView={handleMessageView}
        />

        {paginator?.totalElements > 0 && (
          <TablePaginator page={paginator} onPageChange={handlePageChange} />
        )}

        {isModalOpened && (
          <>
            <IsAuthorized action={RBAC_RULES.CREATE_MESSAGE}>
              <MessageModal
                message={message}
                onRequestClose={handleOnRequestClose}
              />
            </IsAuthorized>

            <IsAuthorized action={RBAC_RULES.CREATE_MESSAGE_REQUEST}>
              <MessageRequestModal
                message={message}
                onRequestClose={handleOnRequestClose}
              />
            </IsAuthorized>
          </>
        )}
      </Card>
    </PageContent>
  )
}

export default Messages
